import { Injectable, NotFoundException } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import {
  StoreConfigResponse,
  StorefrontTemplateSummary,
  ThemeSummary,
} from '@cart/shared-types';
import { BrandingService } from '../branding/branding.service';
import { STOREFRONT_TEMPLATE_SELECT } from '../storefront-templates/storefront-template.select';
import {
  DEFAULT_STOREFRONT_TEMPLATE_SLUG,
  effectivePlan,
  planCoversTier,
} from '../storefront-templates/storefront-template-tier.util';

/**
 * Short by design. This is on the SSR critical path, so the cache exists to
 * absorb the burst of requests a single page render produces — not to hold
 * config for minutes. 30s also bounds how long a theme edit made in admin can
 * stay invisible on the storefront (template changes invalidate eagerly; the
 * theme editor does not know about this cache).
 */
const CACHE_TTL_MS = 30_000;

/**
 * A hard ceiling so a platform with thousands of tenants — or a scan of
 * unknown hosts — cannot grow this map without bound. Expired entries are
 * dropped first; if that is not enough the map is cleared outright, which is
 * safe because every entry is reconstructible from one query.
 */
const MAX_CACHE_ENTRIES = 500;

interface CacheEntry {
  expiresAt: number;
  value: StoreConfigResponse;
}

// Selects isActive/deletedAt on top of the public projection: whether the
// applied template is still *usable* is decided here, and those two columns
// (plus isPublished) are the decision.
const RESOLUTION_TEMPLATE_SELECT = {
  ...STOREFRONT_TEMPLATE_SELECT,
  isActive: true,
  deletedAt: true,
};

const THEME_SELECT = {
  id: true,
  name: true,
  slug: true,
  description: true,
  previewImageUrl: true,
  settings: true,
  isPublished: true,
  createdAt: true,
  updatedAt: true,
};

type ResolvedTemplate = {
  isActive: boolean;
  deletedAt: Date | null;
  isPublished: boolean;
  tier: string;
} & Record<string, unknown>;

/**
 * Resolves everything the storefront needs at boot in one round trip:
 * tenant identity, theme (presentation), and template (structure).
 *
 * Two policies live here and nowhere else:
 *
 * 1. **Unpublish is a global kill switch.** An unpublished, deactivated or
 *    soft-deleted template resolves to `classic`. This is deliberately unlike
 *    themes, where unpublishing only hides a theme from the picker and stores
 *    already on it keep rendering it — a theme is 15 colour tokens and cannot
 *    be "broken", whereas a template is real component code that can be, and
 *    needs instant global rollback with no data migration.
 *
 * 2. **Entitlement lapse never changes rendering.** A downgraded or expired
 *    plan still gets its template back verbatim, with `entitlementLapsed`
 *    raised for the admin banner. Silently reverting a live storefront's
 *    appearance because a card expired is worse than the unpaid-for pixels.
 */
@Injectable()
export class StoreConfigService {
  private readonly cache = new Map<string, CacheEntry>();

  constructor(
    private readonly prisma: PrismaService,
    private readonly branding: BrandingService
  ) {}

  async getConfig(storeId: string, now: number = Date.now()): Promise<StoreConfigResponse> {
    const cached = this.cache.get(storeId);
    if (cached && cached.expiresAt > now) return cached.value;

    const value = await this.load(storeId);
    this.prune(now);
    this.cache.set(storeId, { expiresAt: now + CACHE_TTL_MS, value });
    return value;
  }

  /** Called after a store's template changes so the merchant sees it at once. */
  invalidate(storeId: string): void {
    this.cache.delete(storeId);
  }

  /** Called when a template row changes in a way that affects every tenant. */
  invalidateAll(): void {
    this.cache.clear();
  }

  private async load(storeId: string): Promise<StoreConfigResponse> {
    const store = await this.prisma.store.findUnique({
      where: { id: storeId },
      select: {
        id: true,
        name: true,
        defaultCurrency: true,
        theme: { select: THEME_SELECT },
        storefrontTemplate: { select: RESOLUTION_TEMPLATE_SELECT },
        organization: { select: { plan: true, planExpiresAt: true } },
      },
    });

    if (!store) throw new NotFoundException('Store not found');

    /*
     * Folded into this call rather than given its own endpoint: SEO tags are
     * worthless unless they are in the server-rendered HTML, so branding has to
     * resolve before first paint. A second round trip on the SSR critical path
     * would cost more than the tags are worth.
     *
     * Fails open. A storefront that cannot read its own meta description should
     * still serve its catalogue.
     */
    const branding = await this.branding
      .storefrontBranding(storeId)
      .catch(() => null);

    const applied = store.storefrontTemplate as ResolvedTemplate | null;
    const usable = !!applied && applied.isPublished && applied.isActive && applied.deletedAt === null;
    const resolved = usable ? applied : await this.loadDefaultTemplate();

    const plan = effectivePlan(store.organization?.plan, store.organization?.planExpiresAt);
    const entitlementLapsed = !!resolved && !planCoversTier(plan, resolved.tier);

    return {
      store: { id: store.id, name: store.name, currency: store.defaultCurrency },
      // Dates are serialised to ISO strings by Nest's JSON pipeline, exactly as
      // /store/theme has always returned them. Passed through rather than
      // remapped so this payload stays byte-identical to the two calls it
      // replaces and the storefront needs no second parsing path.
      theme: (store.theme ?? null) as unknown as ThemeSummary | null,
      template: this.toSummary(resolved),
      branding,
      entitlementLapsed,
    };
  }

  /**
   * `classic` is the floor, so it is looked up WITHOUT an `isPublished` filter:
   * unpublishing it would otherwise leave every store with no template at all,
   * turning the kill switch into an outage. Only deletion/deactivation removes
   * it, and then the client's own `'classic'` registry fallback takes over.
   */
  private async loadDefaultTemplate(): Promise<ResolvedTemplate | null> {
    const template = await this.prisma.storefrontTemplate.findFirst({
      where: { slug: DEFAULT_STOREFRONT_TEMPLATE_SLUG, isActive: true, deletedAt: null },
      select: RESOLUTION_TEMPLATE_SELECT,
    });
    return (template ?? null) as ResolvedTemplate | null;
  }

  private toSummary(template: ResolvedTemplate | null): StorefrontTemplateSummary | null {
    if (!template) return null;
    const { isActive: _isActive, deletedAt: _deletedAt, ...summary } = template;
    return summary as unknown as StorefrontTemplateSummary;
  }

  private prune(now: number): void {
    if (this.cache.size < MAX_CACHE_ENTRIES) return;
    for (const [key, entry] of this.cache) {
      if (entry.expiresAt <= now) this.cache.delete(key);
    }
    if (this.cache.size >= MAX_CACHE_ENTRIES) this.cache.clear();
  }
}
